import { useLingui } from '@lingui/react/macro';
import { Tabs, TabsList, TabsTrigger } from '@repo/videa-components/src/index';
import classNames from 'classnames';

import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import annotatorConstants from '../../annotator.constants';

type ImageFilterProps = {
  currentImageFilter: string;
  onSetCurrentImageFilter: (imageType: string) => void;
  numberOfBitewings: number;
  numberOfSortedFMXImages: number;
  visitSummary?: TVisitSummary;
  shouldUseSeriesView?: boolean;
};

export const ImageFilter = ({
  numberOfBitewings,
  numberOfSortedFMXImages,
  currentImageFilter,
  onSetCurrentImageFilter,
  visitSummary,
  shouldUseSeriesView = false,
}: ImageFilterProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const TABS = [
    {
      label: t`FMX`,
      count: numberOfSortedFMXImages,
      imageType: annotatorConstants.FMX,
    },
    {
      label: shouldUseSeriesView ? t`Series` : t`BW`,
      count: numberOfBitewings,
      imageType: annotatorConstants.BITEWING,
    },
  ];

  const handleFilterSelect = (imageType: string) => {
    onSetCurrentImageFilter(imageType);
    trackEvent(mixpanelEvents.imageTypeFilterSelect, {
      selectedFilter: imageType,
      unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
      estimatedVisitId: visitSummary?.estimatedVisitId,
    });
  };

  return (
    <div className="vai-flex vai-items-center vai-bg-background-muted vai-w-fit vai-rounded-md">
      <Tabs value={currentImageFilter}>
        <TabsList className="vai-flex vai-gap-1 vai-bg-transparent vai-p-0">
          {TABS.map(({ label, count, imageType }) => (
            <TabsTrigger
              data-testid={`image-filter-tab-${imageType}`}
              key={label}
              value={imageType}
              onClick={() => handleFilterSelect(imageType)}
              className={classNames(
                'vai-flex vai-items-center vai-gap-2 vai-px-4 vai-py-2',
                'vai-rounded-sm',
                'vai-border-none vai-outline-none focus:vai-outline-none',
                'vai-text-base vai-font-sans vai-font-sm vai-text-foreground-muted',
                'vai-bg-transparent hover:vai-bg-zinc-950',
                'data-[state=active]:vai-bg-zinc-950 data-[state=active]:vai-text-white data-[state=active]:vai-shadow-md',
                'vai-transition-colors vai-duration-200',
                'vai-shadow-none',
              )}
            >
              <span>{label}</span>
              <span
                className={classNames(
                  'vai-ml-2 vai-inline-flex vai-items-center vai-justify-center vai-rounded-full',
                  'vai-text-xs vai-font-semibold',
                  imageType === currentImageFilter
                    ? 'vai-text-white vai-bg-slate-800'
                    : 'vai-text-foreground-muted vai-bg-zinc-950',
                  'vai-h-5 vai-w-5 vai-p-0',
                )}
              >
                {count}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
};
